import React, {FC, forwardRef, useCallback, useMemo} from 'react';

import {cn} from '@bem-react/classname';
import {Checkbox} from '@gravity-ui/uikit';

import OptionContent from '../OptionContent';
import QuestionLayout, {QuestionLayoutType} from '../QuestionLayout';

import {checkboxQuestionPositions} from './CheckboxQuestion.constants';
import {CheckboxQuestionProps} from './CheckboxQuestion.types';

import './CheckboxQuestion.scss';

const cnCheckboxQuestion = cn('CheckboxQuestion');

const CheckboxQuestion: FC<CheckboxQuestionProps> = forwardRef<
    HTMLDivElement,
    CheckboxQuestionProps
>(function CheckboxQuestion({items, values = [], className, onUpdate, ...props}, ref) {
    const withImage = useMemo(() => items.some((item) => item.image), [items]);

    const handleUpdate = useCallback(
        (optionValue: string) => (selected: boolean) => {
            if (!onUpdate) return;
            if (selected) {
                onUpdate(
                    items
                        .reduce<string[]>((acc, {id}) => {
                            if (values.includes(id)) {
                                acc.push(id);
                            }
                            return acc;
                        }, [])
                        .concat(optionValue),
                );
            } else {
                onUpdate(values.filter((item) => item !== optionValue));
            }
        },
        [onUpdate, values, items],
    );

    return (
        <QuestionLayout
            {...props}
            type={QuestionLayoutType.Column}
            className={cnCheckboxQuestion('', [className])}
            positions={checkboxQuestionPositions}
            control={
                <div
                    className={cnCheckboxQuestion('Control', {withImage})}
                    ref={ref}
                    role="group"
                    aria-labelledby={`${props.id}-label`}
                >
                    {items.map(({id, image, ...item}) => {
                        const checked = values.includes(id);
                        return (
                            <Checkbox
                                {...props}
                                key={id}
                                size="l"
                                checked={checked}
                                onUpdate={handleUpdate(id)}
                                className={cnCheckboxQuestion('Option', {checked, withImage})}
                                content={<OptionContent image={image?.links} {...item} />}
                            />
                        );
                    })}
                </div>
            }
        />
    );
});
export default CheckboxQuestion;
